<template>
  <div class="container">
    <div class="card-deck">
      <div class="card card-shadow">
        <div class="card-body">
          <h5 class="card-title">Manage Family</h5>
          <p class="card-text">
            Families share recipes and lists. Name your family whatever you'd
            like, and invite others to join.
          </p>
          <div class="form-group">
            <label for="family-name">Family Name</label>
            <input
              id="family-name"
              type="text"
              class="form-control"
              placeholder="e.g. John's Family"
              v-model="family.name"
              required
              maxlength="100"
              data-error="Name is required."
            />
          </div>
          <div class="form-group">
            <label for="family-unit-system">Unit System</label>
            <b-form-select
              v-model="family.unit_system"
              :options="unitSystems"
            ></b-form-select>
          </div>
          <div class="form-group">
            <label for="selShoppingDay">My typical shopping day</label>
            <b-form-select
              v-model="family.shopping_day"
              :options="days"
            ></b-form-select>
          </div>
          <b-button
            variant="outline-success"
            @click="save()"
            :disabled="!owner"
          >
            <i class="far fa-save"></i> Save
          </b-button>
        </div>
      </div>

      <div class="card card-shadow">
        <div class="container">
          <div class="row">
            <div class="col-xs-12 text-left">
              <div class="card-body">
                <h5 class="card-title">Members</h5>
                <p class="card-text">
                  Members will appear in this list once they've accepted an
                  invite.
                </p>
                <b-input-group class="mb-3">
                  <b-form-input
                    v-model="invite.email"
                    placeholder="Email Address"
                  ></b-form-input>
                  <b-input-group-append>
                    <b-button
                      variant="outline-success"
                      class="rounded-right"
                      @click="sendInvite"
                      >Send Invite</b-button
                    >
                  </b-input-group-append>
                </b-input-group>
              </div>
            </div>
          </div>
        </div>
        <ul id="family-user-rows" class="list-group mx-2 mb-1">
          <li
            class="list-group-item d-flex justify-content-between align-items-center"
            v-for="(member, index) in memberList"
            :key="'member-' + index"
          >
            <span
              >{{ member.name
              }}<small class="ml-2"
                ><span v-if="member.owner"><i>Owner</i></span
                ><span v-if="!member.accepted"><i>Pending</i></span></small
              ></span
            >
            <div class="member-buttons">
              <b-button
                variant="outline-danger"
                size="sm"
                v-if="owner && member.user_uid !== userProfile.uid"
              >
                <i class="far fa-trash-alt"></i>
              </b-button>
            </div>
          </li>
          <li
            class="list-group-item d-flex justify-content-between align-items-center"
            v-for="(member, index) in family.pending"
            :key="'pending-' + index"
          >
            <span
              >{{ member
              }}<small class="ml-2"
                ><span><i>Pending</i></span></small
              ></span
            >
            <div class="member-buttons">
              <b-button variant="outline-danger" size="sm" v-if="owner">
                <i class="far fa-trash-alt"></i>
              </b-button>
            </div>
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters } from "vuex";
import FirebaseMixin from "@/mixins/Firebase";
import UtilsMixin from "@/mixins/Utils";

export default {
  name: "Family",
  mixins: [FirebaseMixin, UtilsMixin],
  components: {},
  data() {
    return {
      ref: "families",
      bind: [],
      family: {
        name: "",
        unit_system: "imperial",
        shopping_day: "sunday",
        members: {},
        pending: []
      },
      unitSystems: [
        { text: "Imperial", value: "imperial" },
        { text: "Metric", value: "metric" }
      ],
      days: [
        { text: "Sunday", value: "sunday" },
        { text: "Monday", value: "monday" },
        { text: "Tuesday", value: "tuesday" },
        { text: "Wednesday", value: "wednesday" },
        { text: "Thursday", value: "thursday" },
        { text: "Friday", value: "friday" },
        { text: "Saturday", value: "saturday" }
      ],
      invite: {
        email: null
      }
    };
  },
  computed: {
    ...mapState(["isAuthenticated", "userProfile"]),
    ...mapGetters(["familyOwner"]),
    familyKey() {
      return this.userProfile.family_key;
    },
    memberList() {
      const members = [];
      for (const [key, value] of Object.entries(this.family.members)) {
        const member = {
          user_uid: key,
          ...value
        };
        members.push(member);
      }
      return members;
    },
    owner() {
      return this.familyOwner;
    }
  },
  watch: {
    familyKey: {
      immediate: true,
      handler(family_key) {
        console.log(family_key);
        if (family_key !== null) {
          this.bindObject("families", family_key, "family");
        }
      }
    },
    family: {
      immediate: false,
      handler(family) {
        if (!("shopping_day" in family)) {
          family.shopping_day = "sunday";
        }
        if (!Array.isArray(family.pending)) {
          family.pending = [];
        }
      }
    }
  },
  methods: {
    save() {
      const family = { ...this.family };
      this.updateObject(this.ref, this.familyKey, family)
        .then(() => {
          window.toastr.success("Family saved successfully.");
        })
        .catch(error => {
          console.log(error);
          window.toastr.error(
            "An error occurred while saving. Please try again later."
          );
        });
    },
    sendInvite() {
      const email = this.invite.email;
      const pending = [...this.family.pending];
      pending.push(email);
      this.updateObject(
        "families/" + this.familyKey + "/pending",
        null,
        pending,
        true
      )
        .then(() => {
          this.invite.email = null;
          window.toastr.success("Invite sent successfully.");
        })
        .catch(error => {
          console.log(error);
          window.toastr.error(
            "An error occurred while sending invite. Please try again later."
          );
        });
    }
  },
  created() {}
};
</script>

<style scoped>
.rounded-right {
  border-top-right-radius: 0.25rem;
  border-bottom-right-radius: 0.25rem;
}
.member-buttons {
  height: 31px;
}
</style>
